import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { ApiResponse } from '@core/models';
import { environment } from '@environments/environment';
import { Observable, catchError, map, throwError } from 'rxjs';
import { ApiResponseError } from '../models/api-response.model';
import { ProvenueErrorService } from './provenue-error.service';

export class ApiException extends Error {
  public code: ApiResponseError['code'];
  public data: ApiResponseError['data'];

  constructor(error: ApiResponseError) {
    super();
    this.message = error?.message;
    this.code = error?.code;
    this.data = error?.data;
  }
}

export class BaseConnectionService {
  #provenueErrorService = inject(ProvenueErrorService);

  apiURL: string = environment.apiUrl;
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': `application/json`,
    }),
  };

  protected handleApiResponse<T>(
    observable: Observable<ApiResponse<T>>
  ): Observable<ApiResponse<T>> {
    return observable.pipe(
      map(obj => {
        if (obj.error) {
          throw new ApiException(obj.error);
        }

        return obj;
      }),
      catchError((error: unknown) => {
        // A business error the map() above already wrapped: rethrow it verbatim. Without
        // this guard it falls into the HttpErrorResponse handling below, where error.error
        // is undefined on an ApiException — the old `error.error.error` then crashed with a
        // TypeError that destroyed the real server message and made callers'
        // `error instanceof ApiException` modal branches fall through to generic text.
        if (error instanceof ApiException) {
          return throwError(() => error);
        }

        console.error('API ERROR: ', error);
        const httpErrorResponse = error as HttpErrorResponse;
        // `?.` on headers too: non-HttpErrorResponse rejections (aborted fetches, timeouts)
        // have none, and reading .get off undefined crashed the whole error chain here.
        const pvRequestId =
          httpErrorResponse?.headers?.get('pv-Request-Id') ?? null;
        const pvProxyRequestId =
          httpErrorResponse?.headers?.get('pv-Proxy-Request-Id') ?? null;
        this.#provenueErrorService.setPvRequestId(pvRequestId);
        this.#provenueErrorService.setPvProxyRequestId(pvProxyRequestId);
        this.#provenueErrorService.logProvenueIds();

        const apiError: ApiResponseError =
          httpErrorResponse?.error?.error ??
          (typeof httpErrorResponse?.error === 'string'
            ? {
                code: String(httpErrorResponse.status),
                message: httpErrorResponse.error,
                data: null,
              }
            : {
                code: String(httpErrorResponse?.status ?? 'network_error'),
                message:
                  httpErrorResponse?.statusText ||
                  'The server could not be reached.',
                data: null,
              });
        return throwError(() => new ApiException(apiError));
      })
    );
  }

  protected sendRequest<T>(
    http: HttpClient,
    endpoint: string,
    body: any = null,
    context?: HttpContext
  ) {
    const finalUrl = `${endpoint}`;
    const options = context
      ? { ...this.httpOptions, context }
      : this.httpOptions;
    const observable = body
      ? http.post<ApiResponse<T>>(finalUrl, body, options)
      : http.get<ApiResponse<T>>(finalUrl, options);
    return this.handleApiResponse(observable);
  }
}
